import type {
    AxiosError,
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
    InternalAxiosRequestConfig,
} from "axios";
import { routing } from "@i18n/routing";

// Relative URLs and absolute URLs matching the current origin are same-origin;
// anything on another host (commercetools, external APIs) is not.
export function isSameOriginRequest(url?: string): boolean {
    if (!url) return true;
    if (typeof window === "undefined") return true;
    try {
        return new URL(url, window.location.origin).origin === window.location.origin;
    } catch {
        return false;
    }
}

// Next stamps the deploymentId of the deployment that served the page on <html>; absent
// when the build has none (local dev).
export function getPageDeploymentId(): string | undefined {
    if (typeof document === "undefined") return undefined;
    return document.documentElement.dataset.dplId || undefined;
}

// Headers only our own origin should see. Sending them to cross-origin hosts (e.g.
// commercetools) triggers a CORS preflight they reject.
export function addSameOriginHeaders(
    config: InternalAxiosRequestConfig,
    deploymentId: string | undefined = getPageDeploymentId()
): InternalAxiosRequestConfig {
    if (!isSameOriginRequest(config.url)) return config;

    config.headers.set("X-Ecco-Locale", routing.defaultLocale);
    // Skew Protection pins only framework requests; this pins our own /api calls the same way.
    if (deploymentId) {
        config.headers.set("x-deployment-id", deploymentId);
    }
    return config;
}

export class AxiosService {
    private axiosInstancePromise: Promise<AxiosInstance> | null = null;

    // Lazily import axios on first request so it stays out of the initial client bundle.
    private getAxiosInstance(): Promise<AxiosInstance> {
        if (!this.axiosInstancePromise) {
            this.axiosInstancePromise = import("axios").then(({ default: axios }) => {
                const instance = axios.create();

                // Intercept the request to add data to the headers: https://axios-http.com/docs/interceptors
                instance.interceptors.request.use((config) => addSameOriginHeaders(config));

                return instance;
            });
        }

        return this.axiosInstancePromise;
    }

    public async get<TResponse>(url: string, config?: AxiosRequestConfig) {
        try {
            return await this.customRequest<TResponse>({
                method: "GET",
                url,
                ...config,
            });
        } catch (error) {
            return this.handleError(error);
        }
    }

    public async post<TPayload, TResponse>(
        url: string,
        data: TPayload,
        config?: AxiosRequestConfig
    ) {
        try {
            return await this.customRequest<TResponse>({
                method: "POST",
                url,
                data,
                headers: {
                    "Content-Type": "application/json",
                    ...config?.headers,
                },
                ...config,
            });
        } catch (error) {
            return this.handleError(error);
        }
    }

    public async delete<TResponse, TPayload = unknown>(
        url: string,
        data?: TPayload,
        config?: AxiosRequestConfig
    ) {
        try {
            return await this.customRequest<TResponse>({
                method: "DELETE",
                url,
                data,
                headers: {
                    "Content-Type": "application/json",
                    ...config?.headers,
                },
                ...config,
            });
        } catch (error) {
            return this.handleError(error);
        }
    }

    public async put<TResponse, TPayload = unknown>(
        url: string,
        data: TPayload,
        config?: AxiosRequestConfig
    ) {
        try {
            return await this.customRequest<TResponse>({
                method: "PUT",
                url,
                data,
                headers: {
                    "Content-Type": "application/json",
                    ...config?.headers,
                },
                ...config,
            });
        } catch (error) {
            return this.handleError(error);
        }
    }

    private async customRequest<TResponse>(config: AxiosRequestConfig): Promise<TResponse> {
        const axiosInstance = await this.getAxiosInstance();
        const response = await axiosInstance(config);
        return this.handleResponse<TResponse>(response);
    }

    private handleResponse<TResponse>(response: AxiosResponse): TResponse {
        // TODO: Check if there is consistency in the response structure to avoid this condition
        return response?.data?.data ?? response?.data;
    }

    private handleError(error: unknown): Promise<never> {
        const axiosError = error as AxiosError<{ error?: string; errors?: string[] }>;
        if (axiosError?.isAxiosError) {
            const status = axiosError.response?.status ?? 500;
            const errorMessage =
                axiosError.response?.data?.error ||
                axiosError.response?.data?.errors ||
                axiosError.message;
            const responseData = axiosError.response?.data;

            return Promise.reject({ error: errorMessage, status, data: responseData });
        }
        return Promise.reject({ error: "Unknown error occurred", status: 500 });
    }
}

export const ClientFetchService = new AxiosService();
